import {
  atlasStorageCounts,
  buildBugReport,
  bugReportFileName,
  type EventLog,
} from "../services/event-log";
import type { FeatureFlagService } from "../services/feature-flags";

function element<K extends keyof HTMLElementTagNameMap>(
  name: K,
  text?: string,
): HTMLElementTagNameMap[K] {
  const created = document.createElement(name);
  if (text !== undefined) created.textContent = text;
  return created;
}

export interface DiagnosticsOptions {
  readonly activeFileHash: () => string | null;
  readonly appVersion: string;
  readonly flags: FeatureFlagService;
  readonly log: EventLog;
  readonly now: () => string;
}

/**
 * Diagnostics band. R1 Step 15, behind the `diagnostics` flag, default off.
 *
 * Doc 02 section 4: one tap exports the last fifty entries, the current
 * versioned state and the active file hash as a single JSON file **saved to
 * the device**. There is no network and no upload; the trainee sends it
 * however he likes.
 */
export function mountDiagnostics(
  target: HTMLElement,
  options: DiagnosticsOptions,
): void {
  if (!options.flags.isEnabled("diagnostics")) return;

  const band = element("section");
  band.className = "diagnostics-band";
  band.dataset.role = "diagnostics-band";
  band.append(element("h2", "Diagnostics"));

  const count = element("p");
  count.dataset.role = "diagnostics-count";
  const status = element("p");
  status.dataset.role = "diagnostics-status";
  status.className = "diagnostics-status";

  const exportButton = element("button", "Save a bug report to this device");
  exportButton.type = "button";
  exportButton.dataset.role = "diagnostics-export";

  const refresh = (): void => {
    const held = options.log.entries().length;
    const moments = options.log.moments().length;
    // Ruling 84: the timeline is what makes a reported fault locatable in the
    // run, so the band says how much of the session it holds.
    count.textContent = `${String(held)} event${held === 1 ? "" : "s"} and ${String(moments)} session moment${moments === 1 ? "" : "s"} recorded on this device. Nothing is sent anywhere.`;
  };

  exportButton.addEventListener("click", () => {
    const capturedAt = options.now();
    let countsByType: Readonly<Record<string, number>> = {};
    try {
      countsByType = atlasStorageCounts(Object.keys(window.localStorage));
    } catch (failure: unknown) {
      // Never silent. Storage can be blocked; the report still saves.
      status.textContent =
        `Storage keys could not be read, so the report counts none. ${failure instanceof Error ? failure.message : ""}`.trim();
      status.dataset.status = "error";
    }

    const report = buildBugReport(
      options.log,
      {
        activeFileHash: options.activeFileHash(),
        appVersion: options.appVersion,
        featureFlags: options.flags.snapshot(),
        storedRecordsByType: countsByType,
      },
      capturedAt,
    );

    // Local file only. No network path exists here by construction.
    const blob = new Blob([JSON.stringify(report, null, 2)], {
      type: "application/json",
    });
    const url = URL.createObjectURL(blob);
    const link = element("a");
    link.href = url;
    link.download = bugReportFileName(capturedAt);
    link.click();
    URL.revokeObjectURL(url);
    if (status.dataset.status !== "error") {
      status.textContent = `Saved ${bugReportFileName(capturedAt)} to your downloads. It carries the file hash, never the file.`;
      status.dataset.status = "ready";
    }
    refresh();
  });

  refresh();
  band.append(count, exportButton, status);
  target.append(band);
}
